import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FilePdf, HandCoins, PaperPlaneTilt, Prohibit, Receipt, Wallet } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@/components/ui/dialog";
import { AdminDialogHeading } from "@/components/admin/AdminDialogHeading";
import { cn } from "@/lib/utils";
import { throwIfNotOk } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { formatDateTimeBs } from "@/lib/format";
import { COMPANY_PAYMENT_REQUEST_STATUS_CONFIG, COMPANY_PAYMENT_REQUEST_TYPE_LABEL } from "@/lib/status-configs";
import type { CompanyPaymentRequest } from "@/types";
import { RechargeDialog, toastRecharge, type RechargeResponse } from "./RechargeDialog";
import { InvoiceDetailDialog } from "./InvoiceDetailDialog";
import { fmtKm } from "./invoice-format";

async function post(url: string, body: unknown, fallback: string) {
  const res = await fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  await throwIfNotOk(res, fallback);
  return res.json();
}

export function PaymentRequestsSection({ companyId }: { companyId: string }) {
  const queryClient = useQueryClient();
  const [recharging, setRecharging] = useState<CompanyPaymentRequest | null>(null);
  const [rejecting, setRejecting] = useState<CompanyPaymentRequest | null>(null);
  const [reason, setReason] = useState("");
  const [invoiceId, setInvoiceId] = useState<string | null>(null);

  const { data: requests = [] } = useQuery({
    queryKey: ["admin", "company", companyId, "payment-requests"],
    queryFn: async () => {
      const res = await fetch(`/api/companies/${companyId}/payment-requests`);
      await throwIfNotOk(res, "Greška pri učitavanju zahtjeva");
      return res.json() as Promise<CompanyPaymentRequest[]>;
    },
  });

  const refresh = () => {
    for (const key of ["payment-requests", "invoices", "credit-transactions"]) {
      queryClient.invalidateQueries({ queryKey: ["admin", "company", companyId, key] });
    }
    queryClient.invalidateQueries({ queryKey: ["admin", "company", companyId] });
    queryClient.invalidateQueries({ queryKey: ["admin", "companies"] });
  };

  const proforma = useMutation({
    mutationFn: (r: CompanyPaymentRequest) => post(`/api/company-payment-requests/${r.id}/proforma`, {}, "Predračun nije poslan"),
    onSuccess: () => { toast.success("Predračun poslan firmi"); refresh(); },
  });
  const interim = useMutation({
    mutationFn: (r: CompanyPaymentRequest) =>
      post(`/api/company-payment-requests/${r.id}/interim-invoice`, {}, "Nacrt nije napravljen") as Promise<{ invoiceId: string }>,
    onSuccess: (d) => { refresh(); setInvoiceId(d.invoiceId); },
  });
  const recharge = useMutation({
    mutationFn: (input: { amount: number; note?: string }) =>
      post(`/api/companies/${companyId}/credit/recharge`, { ...input, requestId: recharging!.id }, "Greška pri dopuni"),
    onSuccess: (d) => { toastRecharge(d as RechargeResponse, "Kredit dopunjen, zahtjev zatvoren"); refresh(); setRecharging(null); },
  });
  const reject = useMutation({
    mutationFn: () => post(`/api/company-payment-requests/${rejecting!.id}/reject`, { reason: reason.trim() }, "Zahtjev nije odbijen"),
    onSuccess: () => { toast.success("Zahtjev odbijen, supervizor je obaviješten"); refresh(); setRejecting(null); setReason(""); },
  });

  if (requests.length === 0) return null;

  return (
    <div className="rounded-xl border border-border/60 bg-card p-5 shadow-sm">
      <div className="flex items-center gap-2">
        <HandCoins className="size-4 text-muted-foreground" />
        <h2 className="text-base font-semibold text-foreground">Zahtjevi za plaćanje</h2>
      </div>
      <ul className="mt-3 divide-y divide-border/50">
        {requests.map((r) => {
          const cfg = COMPANY_PAYMENT_REQUEST_STATUS_CONFIG[r.status];
          const pending = r.status === "pending";
          return (
            <li key={r.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">
                  {COMPANY_PAYMENT_REQUEST_TYPE_LABEL[r.type]}
                  {r.amount != null && <span className="ml-1.5 tabular-nums">{fmtKm(r.amount)}</span>}
                </p>
                <p className="text-xs text-muted-foreground">
                  {r.requestedByName ?? "Supervizor"} · {formatDateTimeBs(r.createdAt)}
                  {r.proformaNumber && ` · predračun ${r.proformaNumber}`}
                </p>
                {r.note && <p className="mt-0.5 text-xs text-muted-foreground">"{r.note}"</p>}
                {r.status === "rejected" && r.resolution && <p className="mt-0.5 text-xs text-rose-700">Razlog: {r.resolution}</p>}
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className={cn("mr-1 inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-semibold", cfg.bg, cfg.color)}>
                  <span className={cn("size-1.5 rounded-full", cfg.dot)} />
                  {cfg.label}
                </span>
                {r.proformaNumber && (
                  <Button size="sm" variant="ghost" asChild>
                    <a href={`/api/company-payment-requests/${r.id}/proforma.pdf`} target="_blank" rel="noreferrer">
                      <FilePdf className="size-4" /> Predračun
                    </a>
                  </Button>
                )}
                {r.invoiceId && (
                  <Button size="sm" variant="ghost" onClick={() => setInvoiceId(r.invoiceId)}>
                    <Receipt className="size-4" /> Faktura
                  </Button>
                )}
                {pending && r.type === "credit_topup" && (
                  <>
                    <Button size="sm" variant="outline" disabled={proforma.isPending} onClick={() => proforma.mutate(r)}>
                      <PaperPlaneTilt className="size-4" /> {r.proformaNumber ? "Pošalji ponovo" : "Pošalji predračun"}
                    </Button>
                    <Button size="sm" onClick={() => setRecharging(r)}>
                      <Wallet className="size-4" /> Dopuni kredit
                    </Button>
                  </>
                )}
                {pending && r.type === "interim_invoice" && (
                  <Button size="sm" disabled={interim.isPending} onClick={() => interim.mutate(r)}>
                    <Receipt className="size-4" /> Napravi fakturu
                  </Button>
                )}
                {pending && (
                  <Button size="sm" variant="ghost" onClick={() => setRejecting(r)}>
                    <Prohibit className="size-4" /> Odbij
                  </Button>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {recharging && (
        <RechargeDialog
          open
          initialAmount={recharging.amount}
          description="Upišite iznos koji je stvarno uplaćen. Dopuna zatvara zahtjev supervizora, a firmi se izdaje i šalje faktura."
          onClose={() => setRecharging(null)}
          onSubmit={(d) => recharge.mutate(d)}
          isPending={recharge.isPending}
        />
      )}
      {rejecting && (
        <Dialog open onClose={() => setRejecting(null)} className="sm:max-w-md">
          <DialogHeader onClose={() => setRejecting(null)}>
            <AdminDialogHeading icon={Prohibit} title="Odbij zahtjev" description="Supervizor dobija email sa razlogom." />
          </DialogHeader>
          <DialogBody>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Razlog" autoFocus />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejecting(null)}>Otkaži</Button>
            <Button variant="destructive" disabled={!reason.trim() || reject.isPending} onClick={() => reject.mutate()}>
              Odbij
            </Button>
          </DialogFooter>
        </Dialog>
      )}
      {invoiceId && <InvoiceDetailDialog invoiceId={invoiceId} companyId={companyId} onClose={() => setInvoiceId(null)} />}
    </div>
  );
}
